/**
 * An invoice as a page, `/invoices/:invoiceNo` (docs/features/order-to-invoice/
 * 50_finance_sales_ledger_module_spec.md §9 item 3): its header, the lines it
 * purchase invoices, the money, the payments applied to it and the credit notes raised on
 * it, with Raise credit note for finance while anything is left to credit.
 *
 * No mock-up was drawn for this page: it is the smallest page consistent with
 * the order page's Invoice step. Every word of a state is the backend's.
 * `?raise=1` opens the dialog on arrival: the Invoice step's row links here.
 */
import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { InvoicePage as InvoicePageView } from "@shared-core/data/sales-ledger-types";
import {
  formatQuotedPrice,
  formatShortDateOrDash,
} from "@shared-core/domain/formatters";
import { orderStepFailure } from "@shared-core/domain/order-to-invoice-presentation";
import { paths } from "@shared-core/routing/routes";
import {
  CreditNotesBlock,
  DocumentPageStatus,
  MoneySummary,
  PaymentsBlock,
  Section,
  StateBadge,
  headerFacts,
  useDocumentPage,
  usePdfDownload,
} from "../app/components/domain/sales-ledger/DocumentParts";
import {
  RecordDisputeDialog,
  ResolveDisputeDialog,
} from "../app/components/domain/credit-control/DisputeDialogs";
import { InvoiceDisputePanel } from "../app/components/domain/credit-control/InvoiceDisputePanel";
import { RaiseCreditNoteDialog } from "../app/components/domain/sales-ledger/RaiseCreditNoteDialog";
import { ObjectHeader } from "../app/components/patterns/ObjectHeader";
import { Badge } from "../app/components/primitives/badge";
import { Button } from "../app/components/primitives/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";

export function SalesInvoicePage() {
  const { invoiceNo: rawNo } = useParams<{ invoiceNo: string }>();
  const invoiceNo = rawNo ? decodeURIComponent(rawNo) : "";
  const adapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const [searchParams] = useSearchParams();
  const { state, reload } = useDocumentPage(
    () => adapter.getInvoicePage(invoiceNo),
    [adapter, invoiceNo],
  );
  const pdf = usePdfDownload();
  const [isRaising, setIsRaising] = useState(false);
  const [disputeDialog, setDisputeDialog] = useState<
    "record" | "resolve" | null
  >(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // The row on the order page links here to credit: open the dialog once the page can offer it.
  const hasOpenedFromLink = useRef(false);
  useEffect(() => {
    if (
      state.kind === "loaded" &&
      searchParams.get("raise") === "1" &&
      !hasOpenedFromLink.current &&
      state.page.credit_options &&
      state.page.invoice.raise_credit_note_url
    ) {
      hasOpenedFromLink.current = true;
      setIsRaising(true);
    }
  }, [state, searchParams]);

  if (state.kind !== "loaded")
    return (
      <DocumentPageStatus state={state} what="invoice" documentNo={invoiceNo} />
    );
  const page: InvoicePageView = state.page;
  const { invoice, header } = page;
  const canRaise = Boolean(
    page.credit_options && invoice.raise_credit_note_url,
  );

  return (
    <div className="space-y-6">
      <ObjectHeader
        crumbs={[
          { label: "Customers", to: paths.customers() },
          {
            label: header.customer_name ?? header.customer_code,
            to: paths.customerDetail({ customerId: header.customer_code }),
          },
          {
            label: "Invoices",
            to: paths.customerInvoices({ customerId: header.customer_code }),
          },
          { label: invoice.invoice_no },
        ]}
        title={`Invoice ${invoice.invoice_no}`}
        pills={
          <>
            <StateBadge state={invoice.state} label={invoice.state_label} />
            {page.dispute && (
              <Badge variant="warning" className="font-normal">
                Disputed
              </Badge>
            )}
          </>
        }
        value={formatQuotedPrice(invoice.gross_pence)}
        valueLabel="VAT included"
        facts={[
          ...headerFacts(header),
          { label: "Issued", value: formatShortDateOrDash(invoice.issued_on) },
          { label: "Due", value: formatShortDateOrDash(invoice.due_on) },
        ]}
        actions={
          <>
            <Button
              size="sm"
              variant="outline"
              disabled={pdf.busy}
              onClick={() => void pdf.viewPdf(invoice.pdf_url)}
            >
              View PDF
            </Button>
            {invoice.xero_url && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => capabilities.openExternalUrl(invoice.xero_url!)}
              >
                Open in Xero
              </Button>
            )}
            {canRaise && (
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => setIsRaising(true)}
              >
                Raise credit note…
              </Button>
            )}
            {!page.dispute && page.record_dispute_url && (
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => setDisputeDialog("record")}
              >
                Record dispute…
              </Button>
            )}
          </>
        }
      />

      {(pdf.notice ?? notice) && (
        <p role="status" className="text-muted-foreground text-sm">
          {pdf.notice ?? notice}
        </p>
      )}
      {invoice.held_reason && (
        <p role="status" className="text-sm text-amber-700 dark:text-amber-300">
          {invoice.held_reason}
        </p>
      )}

      {page.dispute && (
        <InvoiceDisputePanel
          dispute={page.dispute}
          onResolve={() => setDisputeDialog("resolve")}
        />
      )}

      <Section title="Lines">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Line</TableHead>
              <TableHead className="text-right">Quantity</TableHead>
              <TableHead className="text-right">Net</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoice.lines.map((line) => (
              <TableRow key={line.line_no}>
                <TableCell>
                  <span className="text-muted-foreground tabular-nums">
                    {line.line_no}
                  </span>{" "}
                  {line.description}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {line.quantity.toLocaleString("en-GB")}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatQuotedPrice(line.net_pence)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <MoneySummary
          ariaLabel="Invoice total"
          rows={[
            { label: "Net", pence: invoice.net_pence, muted: true },
            { label: "VAT", pence: invoice.vat_pence, muted: true },
            { label: "Gross", pence: invoice.gross_pence, emphasis: true },
            ...(invoice.credited_gross_pence > 0
              ? [
                  {
                    label: "Credited",
                    pence: -invoice.credited_gross_pence,
                  },
                ]
              : []),
          ]}
        />
      </Section>

      <PaymentsBlock payments={page.payments} />
      <CreditNotesBlock creditNotes={page.credit_notes} />

      {disputeDialog === "record" && page.record_dispute_url && (
        <RecordDisputeDialog
          invoice={{
            invoiceNo: invoice.invoice_no,
            orderNo: header.order_no ?? null,
            dueOn: invoice.due_on,
            payerName:
              header.payer_name ?? header.customer_name ?? header.customer_code,
            amountPence: invoice.gross_pence,
            amountLabel: "Invoice total",
          }}
          onClose={() => setDisputeDialog(null)}
          onSubmit={async (reason) => {
            await adapter.recordDispute(
              {
                invoice_no: invoice.invoice_no,
                record_dispute_url: page.record_dispute_url,
              },
              { reason },
            );
            setNotice(`Dispute recorded on ${invoice.invoice_no}.`);
            await reload();
          }}
        />
      )}
      {disputeDialog === "resolve" && page.dispute && (
        <ResolveDisputeDialog
          dispute={{
            invoiceNo: invoice.invoice_no,
            amountPence: invoice.gross_pence,
            raisedAt: page.dispute.raised_at,
            raisedByName: page.dispute.raised_by_name,
            reason: page.dispute.reason,
          }}
          onClose={() => setDisputeDialog(null)}
          onSubmit={async (note) => {
            await adapter.resolveDispute(page.dispute!, { note });
            setNotice(`Dispute resolved on ${invoice.invoice_no}.`);
            await reload();
          }}
        />
      )}

      {isRaising && page.credit_options && (
        <RaiseCreditNoteDialog
          invoiceNo={invoice.invoice_no}
          options={page.credit_options}
          initialLineIds={
            searchParams.get("line") ? [searchParams.get("line")!] : []
          }
          busy={busy}
          onClose={() => setIsRaising(false)}
          onRaise={async (input) => {
            setBusy(true);
            try {
              const raised = await adapter.raiseCreditNote(invoice, input);
              setNotice(raised.summary);
              await reload();
              return null;
            } catch (err) {
              return orderStepFailure(err);
            } finally {
              setBusy(false);
            }
          }}
        />
      )}
    </div>
  );
}
